import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Mic, Square, Send, Trash2 } from "lucide-react";
import { queryClient } from "@/lib/queryClient";
import { format } from "date-fns";
import { NotificationSound } from "@/lib/notificationSound";
import { getAuthToken } from "@/lib/authToken";

interface PendingQuestion {
  id: string;
  restaurantId: string;
  customerSessionId: string;
  question: string;
  language: string;
  status: 'pending' | 'answered';
  tableNumber: string | null;
  createdAt: string;
}

export function PendingQuestionsPanel({ restaurantId }: { restaurantId: string }) {
  const [selectedQuestion, setSelectedQuestion] = useState<PendingQuestion | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const { toast } = useToast();
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Fetch pending questions
  const {
    data: questions = [],
    isLoading,
  } = useQuery<PendingQuestion[]>({
    queryKey: ['/api/chef/questions'],
    refetchInterval: 5000, // Poll every 5 seconds for new questions
  });

  // Listen for WebSocket messages
  useEffect(() => {
    if (!restaurantId) {
      return; // Don't connect until we have restaurant ID
    }

    // Determine WebSocket URL
    // 1. Prefer explicit VITE_WS_URL from environment
    // 2. Fallback to localhost:3006 for local development (serverless-offline default)
    // 3. In production, do not attempt to connect to the frontend host (Amplify)

    let wsBaseUrl = (import.meta as any).env.VITE_WS_URL;

    if (!wsBaseUrl) {
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        wsBaseUrl = 'ws://localhost:3006';
      } else {
        console.warn('[PendingQuestionsPanel] VITE_WS_URL not configured. WebSocket disabled (polling only).');
        return;
      }
    }

    const wsUrl = `${wsBaseUrl}?restaurantId=${restaurantId}&role=chef`;

    console.log(`[PendingQuestionsPanel] Attempting connection to: ${wsUrl}`);
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('[PendingQuestionsPanel] WebSocket connected successfully');
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log('[PendingQuestionsPanel] Received message:', message.type);

        if (message.type === 'new-question') {
          // Refresh questions list
          queryClient.invalidateQueries({ queryKey: ['/api/chef/questions'] });

          // Play notification sound
          NotificationSound.playChime();

          toast({
            title: "New Question",
            description: "A customer has a question that needs your answer!",
          });
        }
      } catch (err) {
        console.error('[PendingQuestionsPanel] Error parsing message:', err);
      }
    };

    ws.onerror = (error) => {
      console.error('[PendingQuestionsPanel] Connection error:', error);
    };

    ws.onclose = (event) => {
      console.log('[PendingQuestionsPanel] Connection closed:', event.code, event.reason);
    };

    return () => {
      ws.close();
    };
  }, [restaurantId, toast]);

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
        setIsRecording(false);

        // Auto-send immediately after recording stops
        sendAnswerMutation.mutate(blob);
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      toast({
        title: "Error",
        description: "Failed to access microphone",
        variant: "destructive",
      });
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Send answer mutation
  const sendAnswerMutation = useMutation({
    mutationFn: async (blobOverride?: Blob) => {
      if (!selectedQuestion) {
        throw new Error('No question selected');
      }

      const blob = blobOverride || audioBlob;

      if (!blob) {
        throw new Error('No audio recorded');
      }

      const formData = new FormData();
      formData.append('questionId', selectedQuestion.id);
      formData.append('language', selectedQuestion.language);
      formData.append('audio', blob, 'answer.webm');

      const apiBaseUrl = (import.meta as any).env.VITE_API_BASE_URL || '';

      // Include auth token for Safari compatibility
      const headers: Record<string, string> = {};
      try {
        const token = await getAuthToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (e) {
        console.warn('[PendingQuestions] Failed to get auth token:', e);
      }

      const response = await fetch(`${apiBaseUrl}/api/chef/answer`, {
        method: 'POST',
        headers,
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send answer');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/chef/questions'] });
      queryClient.invalidateQueries({ queryKey: ['/api/faqs'] });
      setSelectedQuestion(null);
      setAudioBlob(null);
      toast({
        title: "Success",
        description: "Your answer has been sent to the customer!",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete question mutation
  const deleteQuestionMutation = useMutation({
    mutationFn: async (id: string) => {
      const apiBaseUrl = (import.meta as any).env.VITE_API_BASE_URL || '';

      // Include auth token for Safari compatibility
      const headers: Record<string, string> = {};
      try {
        const token = await getAuthToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (e) {
        console.warn('[PendingQuestions] Failed to get auth token:', e);
      }

      const response = await fetch(`${apiBaseUrl}/api/chef/questions/${id}`, {
        method: 'DELETE',
        headers,
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to delete question');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/chef/questions'] });
      toast({
        title: "Deleted",
        description: "The question has been removed.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const pendingQuestions = questions.filter(q => q.status === 'pending');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" data-testid="loader-questions" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Customer Questions</h2>
        <p className="text-muted-foreground mt-1">
          Answer customer questions via voice - your responses will be sent back instantly
        </p>
      </div>

      {pendingQuestions.length === 0 ? (
        <Card className="p-12">
          <p className="text-muted-foreground text-center" data-testid="text-no-questions">
            No pending questions. When customers ask questions that the AI can't answer, they'll appear here.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pendingQuestions.map((question) => (
            <Card key={question.id} className="p-6 space-y-4" data-testid={`card-question-${question.id}`}>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">
                    {format(new Date(question.createdAt), 'MMM d, h:mm a')}
                  </span>
                  <div className="flex gap-2 items-center">
                    {question.tableNumber && (
                      <span className="text-xs px-2 py-1 rounded bg-orange-100 text-orange-700 font-bold">
                        TABLE: {question.tableNumber}
                      </span>
                    )}
                    <span className="text-xs px-2 py-1 rounded bg-primary/10 text-primary">
                      {question.language.toUpperCase()}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm('Are you sure you want to delete this question?')) {
                          deleteQuestionMutation.mutate(question.id);
                        }
                      }}
                      data-testid={`button-delete-${question.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <p className="font-medium text-lg mb-2" data-testid={`text-question-${question.id}`}>
                  {question.question}
                </p>
                <p className="text-xs text-muted-foreground">
                  Session: {question.customerSessionId.substring(0, 8)}...
                </p>
              </div>

              {selectedQuestion?.id === question.id ? (
                <div className="space-y-4">
                  {/* Voice Control Loop */}
                  {!audioBlob && (
                    <div className="flex flex-col gap-3">
                      <div className={`p-4 rounded-lg flex items-center justify-center ${isRecording ? 'bg-red-50 animate-pulse' : 'bg-secondary'}`}>
                        {isRecording ? (
                          <div className="flex items-center text-red-600 font-bold">
                            <Mic className="h-5 w-5 mr-2" />
                            RECORDING...
                          </div>
                        ) : (
                          <div className="text-muted-foreground flex items-center">
                            <Mic className="h-5 w-5 mr-2" />
                            Ready to record
                          </div>
                        )}
                      </div>

                      <Button
                        onClick={isRecording ? stopRecording : startRecording}
                        className={`h-16 text-lg ${isRecording ? "bg-red-600 hover:bg-red-700" : "bg-primary hover:bg-primary/90"}`}
                        data-testid={`button-record-${question.id}`}
                      >
                        {isRecording ? (
                          <>
                            <Square className="h-5 w-5 mr-2" />
                            STOP RECORDING
                          </>
                        ) : (
                          <>
                            <Mic className="h-5 w-5 mr-2" />
                            START RECORDING
                          </>
                        )}
                      </Button>
                    </div>
                  )}

                  {/* Recorded - Confirmation to send (Optional if Auto-send is on, but helpful for error state) */}
                  {audioBlob && sendAnswerMutation.isError && (
                    <div className="space-y-3">
                      <div className="p-4 bg-red-50 rounded-lg flex items-center justify-center text-red-700 font-medium">
                        Failed to send. Try again?
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => sendAnswerMutation.mutate(undefined)}
                          disabled={sendAnswerMutation.isPending}
                          className="flex-1 h-12 bg-red-600 hover:bg-red-700"
                        >
                          {sendAnswerMutation.isPending ? (
                            <Loader2 className="h-5 w-5 animate-spin mr-2" />
                          ) : (
                            <Send className="h-5 w-5 mr-2" />
                          )}
                          RETRY SEND
                        </Button>
                        <Button
                          onClick={() => setAudioBlob(null)}
                          variant="outline"
                          className="h-12"
                        >
                          Discard
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Processing Status */}
                  {sendAnswerMutation.isPending && (
                    <div className="p-8 bg-blue-50 rounded-lg flex flex-col items-center justify-center gap-3">
                      <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
                      <p className="text-blue-700 font-bold animate-pulse">
                        TRANSCRIBING & SENDING...
                      </p>
                    </div>
                  )}

                  {/* Cancel Entire answering process */}
                  {!isRecording && !sendAnswerMutation.isPending && (
                    <Button
                      onClick={() => {
                        setAudioBlob(null);
                        setSelectedQuestion(null);
                      }}
                      variant="ghost"
                      className="w-full text-muted-foreground"
                      data-testid={`button-cancel-${question.id}`}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              ) : (
                <Button
                  onClick={() => setSelectedQuestion(question)}
                  variant="outline"
                  className="w-full"
                  data-testid={`button-answer-${question.id}`}
                >
                  Answer Question
                </Button>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
